import { useState, useEffect } from 'react'
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, IconButton,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControl, InputLabel, Select, MenuItem,
  CircularProgress, Alert, InputAdornment,
} from '@mui/material'
import {
  PersonAdd, Delete, LockReset, Visibility, VisibilityOff,
} from '@mui/icons-material'
import { useSelector } from 'react-redux'
import { tenantApi } from '../services/api'
import { RootState } from '../store'

const TeamPage = () => {
  const { user } = useSelector((state: RootState) => state.auth)
  const tenantId = user?.tenant_id
  const isTenantAdmin = user?.role === 'admin'

  const [users, setUsers] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Add user dialog
  const [addOpen, setAddOpen] = useState(false)
  const [addForm, setAddForm] = useState({ name: '', email: '', password: '', role: 'viewer' })
  const [addError, setAddError] = useState('')
  const [showPassword, setShowPassword] = useState(false)

  // Reset password dialog
  const [resetOpen, setResetOpen] = useState(false)
  const [resetUserId, setResetUserId] = useState('')
  const [resetPassword, setResetPassword] = useState('')
  const [showResetPassword, setShowResetPassword] = useState(false)

  // Role change request dialog (non-admins)
  const [roleRequestOpen, setRoleRequestOpen] = useState(false)
  const [requestedRole, setRequestedRole] = useState('')
  const [roleRequestReason, setRoleRequestReason] = useState('')

  // Pending requests (admins)
  const [roleRequests, setRoleRequests] = useState<any[]>([])
  const [joinRequests, setJoinRequests] = useState<any[]>([])

  // Teams
  const [teams, setTeams] = useState<any[]>([])
  const [createTeamOpen, setCreateTeamOpen] = useState(false)
  const [newTeamName, setNewTeamName] = useState('')
  const [newTeamDesc, setNewTeamDesc] = useState('')
  const [manageTeamOpen, setManageTeamOpen] = useState(false)
  const [selectedTeam, setSelectedTeam] = useState<any>(null)
  const [teamMembers, setTeamMembers] = useState<any[]>([])
  const [addToTeamUserId, setAddToTeamUserId] = useState('')

  useEffect(() => {
    if (tenantId) fetchAll()
  }, [tenantId])

  const fetchAll = async () => {
    if (!tenantId) return
    setLoading(true)
    try {
      const [membersRes, teamsRes] = await Promise.allSettled([
        tenantApi.getMembers(tenantId),
        tenantApi.getTeams(tenantId),
      ])
      setUsers(membersRes.status === 'fulfilled' ? (membersRes.value.data || []) : [])
      setTeams(teamsRes.status === 'fulfilled' ? (teamsRes.value.data || []) : [])
      if (isTenantAdmin) {
        const [roleRes, joinRes] = await Promise.allSettled([
          tenantApi.getRoleRequests(tenantId),
          tenantApi.getJoinRequests(tenantId),
        ])
        setRoleRequests(roleRes.status === 'fulfilled' ? (roleRes.value.data || []) : [])
        setJoinRequests(joinRes.status === 'fulfilled' ? (joinRes.value.data || []) : [])
      }
    } catch (e) {
      setError('Failed to load team members')
    } finally {
      setLoading(false)
    }
  }

  const handleAddUser = async () => {
    if (!tenantId) return
    setAddError('')
    if (addForm.password.length < 8) {
      setAddError('Password must be at least 8 characters')
      return
    }
    try {
      await tenantApi.createTenantUser(tenantId, addForm)
      setAddOpen(false)
      setAddForm({ name: '', email: '', password: '', role: 'viewer' })
      fetchAll()
    } catch (e: any) {
      setAddError(e.response?.data?.detail || 'Failed to add user')
    }
  }

  const handleDeleteUser = async (userId: string) => {
    if (userId === user?.id) return
    if (!confirm('Remove this team member?')) return
    try {
      await tenantApi.deleteTenantUser(userId)
      fetchAll()
    } catch (e) {
      setError('Failed to remove user')
    }
  }

  const handleResetPassword = async () => {
    if (resetPassword.length < 8) return
    try {
      await tenantApi.resetTenantUserPassword(resetUserId, { new_password: resetPassword })
      setResetOpen(false)
      setResetPassword('')
      setResetUserId('')
    } catch (e: any) {
      alert(e.response?.data?.detail || 'Failed to reset password')
    }
  }

  const handleRoleChange = async (userId: string, newRole: string) => {
    if (userId === user?.id) return
    try {
      await tenantApi.updateTenantUser(userId, { role: newRole })
      fetchAll()
    } catch (e) {
      setError('Failed to update role')
    }
  }

  const handleRoleRequest = async () => {
    if (!tenantId || !requestedRole) return
    try {
      await tenantApi.requestRoleChange(tenantId, { requested_role: requestedRole, reason: roleRequestReason })
      setRoleRequestOpen(false)
      setRequestedRole('')
      setRoleRequestReason('')
      setSuccess('Role change request submitted. Your admin will be notified.')
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to submit request')
    }
  }

  const handleApproveRole = async (requestId: string) => {
    if (!tenantId) return
    try {
      await tenantApi.approveRoleRequest(tenantId, requestId)
      fetchAll()
      setSuccess('Role change approved.')
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to approve')
    }
  }

  const handleRejectRole = async (requestId: string) => {
    if (!tenantId) return
    try {
      await tenantApi.rejectRoleRequest(tenantId, requestId)
      fetchAll()
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to reject')
    }
  }

  const handleApproveJoin = async (requestId: string) => {
    if (!tenantId) return
    try {
      await tenantApi.approveJoinRequest(tenantId, requestId)
      fetchAll()
      setSuccess('Join request approved.')
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to approve')
    }
  }

  const handleRejectJoin = async (requestId: string) => {
    if (!tenantId) return
    try {
      await tenantApi.rejectJoinRequest(tenantId, requestId)
      fetchAll()
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to reject')
    }
  }

  // --- Team handlers ---
  const handleCreateTeam = async () => {
    if (!tenantId || !newTeamName.trim()) return
    try {
      await tenantApi.createTeam(tenantId, { name: newTeamName.trim(), description: newTeamDesc.trim() || null })
      setCreateTeamOpen(false)
      setNewTeamName('')
      setNewTeamDesc('')
      fetchAll()
      setSuccess('Team created.')
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to create team')
    }
  }

  const handleDeleteTeam = async (teamId: string) => {
    if (!tenantId || !confirm('Delete this team? Members will not be removed from the organization.')) return
    try {
      await tenantApi.deleteTeam(tenantId, teamId)
      fetchAll()
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to delete team')
    }
  }

  const openManageTeam = async (team: any) => {
    if (!tenantId) return
    setSelectedTeam(team)
    setAddToTeamUserId('')
    try {
      const res = await tenantApi.getTeamMembers(tenantId, team.id)
      setTeamMembers(res.data || [])
    } catch { setTeamMembers([]) }
    setManageTeamOpen(true)
  }

  const handleAddToTeam = async () => {
    if (!tenantId || !selectedTeam || !addToTeamUserId) return
    try {
      await tenantApi.addTeamMember(tenantId, selectedTeam.id, { user_id: addToTeamUserId })
      setAddToTeamUserId('')
      const res = await tenantApi.getTeamMembers(tenantId, selectedTeam.id)
      setTeamMembers(res.data || [])
      fetchAll()
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to add member')
    }
  }

  const handleRemoveFromTeam = async (userId: string) => {
    if (!tenantId || !selectedTeam) return
    try {
      await tenantApi.removeTeamMember(tenantId, selectedTeam.id, userId)
      const res = await tenantApi.getTeamMembers(tenantId, selectedTeam.id)
      setTeamMembers(res.data || [])
      fetchAll()
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to remove member')
    }
  }

  const handleJoinTeam = async (teamId: string) => {
    if (!tenantId) return
    try {
      await tenantApi.joinTeam(tenantId, teamId)
      fetchAll()
      setSuccess('Joined team.')
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to join team')
    }
  }

  const handleLeaveTeam = async (teamId: string) => {
    if (!tenantId) return
    try {
      await tenantApi.leaveTeam(tenantId, teamId)
      fetchAll()
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to leave team')
    }
  }

  const roleColor = (role: string) => {
    const map: any = { admin: 'error', manager: 'warning', analyst: 'info', viewer: 'default' }
    return map[role] || 'default'
  }

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4">Team</Typography>
          <Typography variant="body2" color="text.secondary">
            {users.length} member{users.length !== 1 ? 's' : ''}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!isTenantAdmin && (
            <Button variant="outlined" onClick={() => setRoleRequestOpen(true)}>
              Request Access Change
            </Button>
          )}
          {isTenantAdmin && (
            <Button variant="contained" startIcon={<PersonAdd />} onClick={() => { setAddError(''); setAddOpen(true) }}>
              Add Member
            </Button>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Pending Requests — admin only */}
      {isTenantAdmin && (roleRequests.length > 0 || joinRequests.length > 0) && (
        <Card sx={{ mb: 2 }}>
          <CardContent>
            {roleRequests.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Pending Role Change Requests</Typography>
                {roleRequests.map((r: any) => (
                  <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2"><strong>{r.user_name}</strong> ({r.user_email})</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {r.current_role} &rarr; {r.requested_role}
                        {r.reason && ` — "${r.reason}"`}
                      </Typography>
                    </Box>
                    <Button size="small" variant="contained" color="success" onClick={() => handleApproveRole(r.id)}>Approve</Button>
                    <Button size="small" variant="outlined" color="error" onClick={() => handleRejectRole(r.id)}>Reject</Button>
                  </Box>
                ))}
              </>
            )}
            {joinRequests.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mt: roleRequests.length > 0 ? 2 : 0, mb: 1 }}>Pending Join Requests</Typography>
                {joinRequests.map((r: any) => (
                  <Box key={r.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2"><strong>{r.name}</strong> ({r.email})</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Requested {new Date(r.created_at).toLocaleDateString()}
                      </Typography>
                    </Box>
                    <Button size="small" variant="contained" color="success" onClick={() => handleApproveJoin(r.id)}>Approve</Button>
                    <Button size="small" variant="outlined" color="error" onClick={() => handleRejectJoin(r.id)}>Reject</Button>
                  </Box>
                ))}
              </>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Login</TableCell>
                  {isTenantAdmin && <TableCell align="right">Actions</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((u: any) => (
                  <TableRow key={u.id}>
                    <TableCell>
                      <Typography variant="body2">
                        {u.name}
                        {u.id === user?.id && (
                          <Chip label="You" size="small" sx={{ ml: 1, height: 18, fontSize: '0.65rem' }} />
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      {isTenantAdmin && u.id !== user?.id ? (
                        <Select
                          size="small" value={u.role}
                          onChange={e => handleRoleChange(u.id, e.target.value)}
                          sx={{ fontSize: '0.85rem', height: 30 }}
                        >
                          <MenuItem value="viewer">Viewer</MenuItem>
                          <MenuItem value="analyst">Analyst</MenuItem>
                          <MenuItem value="manager">Manager</MenuItem>
                          <MenuItem value="admin">Admin</MenuItem>
                        </Select>
                      ) : (
                        <Chip label={u.role} size="small" color={roleColor(u.role) as any} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip label={u.status} size="small"
                        color={u.status === 'active' ? 'success' : u.status === 'locked' ? 'error' : 'default'} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {u.last_login ? new Date(u.last_login).toLocaleString() : 'Never'}
                      </Typography>
                    </TableCell>
                    {isTenantAdmin && (
                      <TableCell align="right">
                        <IconButton size="small" title="Reset Password"
                          onClick={() => { setResetUserId(u.id); setResetPassword(''); setResetOpen(true) }}>
                          <LockReset fontSize="small" />
                        </IconButton>
                        {u.id !== user?.id && (
                          <IconButton size="small" title="Remove" onClick={() => handleDeleteUser(u.id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          <strong>Roles:</strong> Viewer (read-only) | Analyst (run scans, view results) | Manager (manage scanners) | Admin (full access + team management)
        </Typography>
      </Box>

      {/* Teams Section */}
      <Box sx={{ mt: 4, mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Teams</Typography>
        {isTenantAdmin && (
          <Button variant="outlined" size="small" onClick={() => setCreateTeamOpen(true)}>
            Create Team
          </Button>
        )}
      </Box>

      {teams.length === 0 ? (
        <Typography variant="body2" color="text.secondary">No teams created yet.</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          {teams.map((t: any) => {
            const myTeams = users.find((u: any) => u.id === user?.id)?.teams || []
            const isMember = myTeams.includes(t.name)
            return (
              <Card key={t.id} sx={{ minWidth: 250, flex: '1 1 300px', maxWidth: 400 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{t.name}</Typography>
                      {t.description && <Typography variant="body2" color="text.secondary">{t.description}</Typography>}
                    </Box>
                    <Chip label={`${t.member_count} member${t.member_count !== 1 ? 's' : ''}`} size="small" variant="outlined" />
                  </Box>
                  <Box sx={{ mt: 1.5, display: 'flex', gap: 1 }}>
                    {isTenantAdmin && (
                      <>
                        <Button size="small" variant="outlined" onClick={() => openManageTeam(t)}>Manage</Button>
                        <Button size="small" color="error" onClick={() => handleDeleteTeam(t.id)}>Delete</Button>
                      </>
                    )}
                    {!isTenantAdmin && !isMember && (
                      <Button size="small" variant="outlined" onClick={() => handleJoinTeam(t.id)}>Join</Button>
                    )}
                    {!isTenantAdmin && isMember && (
                      <Button size="small" color="warning" onClick={() => handleLeaveTeam(t.id)}>Leave</Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )
          })}
        </Box>
      )}

      {/* Create Team Dialog */}
      <Dialog open={createTeamOpen} onClose={() => setCreateTeamOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Team</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Team Name" margin="normal" required
            value={newTeamName} onChange={e => setNewTeamName(e.target.value)}
            placeholder="e.g. Platform Engineering" />
          <TextField fullWidth label="Description (optional)" margin="normal" multiline rows={2}
            value={newTeamDesc} onChange={e => setNewTeamDesc(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateTeamOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateTeam} disabled={!newTeamName.trim()}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Manage Team Dialog */}
      <Dialog open={manageTeamOpen} onClose={() => setManageTeamOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Manage: {selectedTeam?.name}</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Members</Typography>
          {teamMembers.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>No members yet.</Typography>
          ) : (
            <Box sx={{ mb: 2 }}>
              {teamMembers.map((m: any) => (
                <Box key={m.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, p: 0.5, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ flex: 1 }}>{m.name} ({m.email})</Typography>
                  <Chip label={m.role} size="small" />
                  <IconButton size="small" onClick={() => handleRemoveFromTeam(m.user_id)}><Delete fontSize="small" /></IconButton>
                </Box>
              ))}
            </Box>
          )}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>Add Member</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Select User</InputLabel>
              <Select value={addToTeamUserId} label="Select User"
                onChange={e => setAddToTeamUserId(e.target.value)}>
                {users
                  .filter((u: any) => !teamMembers.some((m: any) => m.user_id === u.id))
                  .map((u: any) => (
                    <MenuItem key={u.id} value={u.id}>{u.name} ({u.email})</MenuItem>
                  ))}
              </Select>
            </FormControl>
            <Button variant="contained" onClick={handleAddToTeam} disabled={!addToTeamUserId}>Add</Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageTeamOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add User Dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Team Member</DialogTitle>
        <DialogContent>
          {addError && <Alert severity="error" sx={{ mb: 2 }}>{addError}</Alert>}
          <TextField fullWidth label="Full Name" margin="normal" required
            value={addForm.name} onChange={e => setAddForm({ ...addForm, name: e.target.value })} />
          <TextField fullWidth label="Email" margin="normal" type="email" required
            value={addForm.email} onChange={e => setAddForm({ ...addForm, email: e.target.value })} />
          <TextField fullWidth label="Temporary Password" margin="normal" required
            type={showPassword ? 'text' : 'password'}
            value={addForm.password}
            onChange={e => setAddForm({ ...addForm, password: e.target.value })}
            helperText="Minimum 8 characters. You will need to share these credentials with the team member directly."
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" size="small">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Role</InputLabel>
            <Select value={addForm.role} label="Role"
              onChange={e => setAddForm({ ...addForm, role: e.target.value })}>
              <MenuItem value="viewer">Viewer — read-only access to dashboards and reports</MenuItem>
              <MenuItem value="analyst">Analyst — can run scans and view results</MenuItem>
              <MenuItem value="manager">Manager — can manage scanners and configurations</MenuItem>
              <MenuItem value="admin">Admin — full access including team management</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddUser}>Add Member</Button>
        </DialogActions>
      </Dialog>

      {/* Role Change Request Dialog (non-admins) */}
      <Dialog open={roleRequestOpen} onClose={() => setRoleRequestOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Request Access Change</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Your current role: <Chip label={user?.role} size="small" color={roleColor(user?.role || '') as any} />
          </Typography>
          <FormControl fullWidth margin="normal">
            <InputLabel>Requested Role</InputLabel>
            <Select value={requestedRole} label="Requested Role"
              onChange={e => setRequestedRole(e.target.value)}>
              {user?.role !== 'analyst' && <MenuItem value="analyst">Analyst — run scans and view results</MenuItem>}
              {user?.role !== 'manager' && <MenuItem value="manager">Manager — manage scanners and configurations</MenuItem>}
              {user?.role !== 'admin' && <MenuItem value="admin">Admin — full access including team management</MenuItem>}
            </Select>
          </FormControl>
          <TextField fullWidth label="Reason (optional)" margin="normal" multiline rows={2}
            value={roleRequestReason} onChange={e => setRoleRequestReason(e.target.value)}
            placeholder="Why do you need this access level?" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoleRequestOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleRoleRequest} disabled={!requestedRole}>Submit Request</Button>
        </DialogActions>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={resetOpen} onClose={() => setResetOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Reset Password</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="New Password" margin="normal" required
            type={showResetPassword ? 'text' : 'password'}
            value={resetPassword}
            onChange={e => setResetPassword(e.target.value)}
            helperText="Minimum 8 characters"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowResetPassword(!showResetPassword)} edge="end" size="small">
                    {showResetPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleResetPassword}
            disabled={resetPassword.length < 8}>Reset Password</Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default TeamPage
